import React, { useState, useEffect } from 'react';
import { useHeader } from '../contexts/HeaderContext';
import { useQuery, useMutation } from 'react-query';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  EyeIcon,
  XMarkIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

interface EmailTemplate {
  alias: string;
  name: string;
  templateId?: number;
  active: boolean;
  editUrl?: string;
}

interface TemplatePreview {
  alias: string;
  templateId?: number;
  htmlContent: string;
  textContent: string;
  subject: string;
  editUrl?: string;
  templateModel: Record<string, any>;
}

interface TemplatesResponse {
  templates: EmailTemplate[];
}

// Fetch available templates
const fetchTemplates = async (): Promise<EmailTemplate[]> => {
  const response = await apiService.get<TemplatesResponse>('/admin/email/templates');
  return response.templates || [];
};

// Fetch template preview
const fetchTemplatePreview = async (alias: string, templateModel: Record<string, any>): Promise<TemplatePreview> => {
  const params = new URLSearchParams({
    model: JSON.stringify(templateModel),
  });
  return apiService.get<TemplatePreview>(`/admin/email/templates/${alias}/preview?${params}`);
};

// Send test email
const sendTestEmail = async (data: { to: string; templateAlias: string; templateModel: Record<string, any> }): Promise<{ success: boolean; message: string }> => {
  return apiService.post<{ success: boolean; message: string }>('/admin/email/test', data);
};

const EmailTesting: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [testEmail, setTestEmail] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<TemplatePreview | null>(null);
  const [templateModel, setTemplateModel] = useState<Record<string, any>>({});
  const { setTitle } = useHeader();

  useEffect(() => {
    setTitle('Email Delivery');
  }, [setTitle]);

  // Fetch templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: fetchTemplates,
  });

  // Fetch preview when template is selected and preview is requested
  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['templatePreview', selectedTemplate, templateModel],
    queryFn: () => fetchTemplatePreview(selectedTemplate, templateModel),
    enabled: !!selectedTemplate && showPreview,
    retry: false,
  });

  // Send test email mutation
  const sendEmailMutation = useMutation({
    mutationFn: sendTestEmail,
    onSuccess: () => {
      toast.success('Test email sent successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send test email');
    },
  });

  // Get default template model based on selected template
  const getDefaultTemplateModel = (alias: string): Record<string, any> => {
    const defaults: Record<string, Record<string, any>> = {
      welcome: {
        firstName: 'John',
        dashboardUrl: 'https://dev.emm.aprco.com/dashboard',
      },
      invitation: {
        inviterName: 'Jane Doe',
        entityName: 'Example Client',
        invitationUrl: 'https://dev.emm.aprco.com/invite/token123',
        expiryDays: 7,
      },
      'delegated-question': {
        userName: 'John Doe',
        assessmentName: 'Q4 2024 Assessment',
        questionText: 'What is your current security posture?',
        assessmentUrl: 'https://dev.emm.aprco.com/assessments/123',
        dueDate: '2024-12-31',
        notes: 'Please review and respond by the due date.',
      },
      'delegation-reminder': {
        userName: 'John Doe',
        assessmentName: 'Q4 2024 Assessment',
        questionText: 'What is your current security posture?',
        dueDate: '2024-12-31',
        assessmentUrl: 'https://dev.emm.aprco.com/assessments/123',
      },
      'assessment-completed': {
        entityName: 'Example Client',
        assessmentName: 'Q4 2024 Assessment',
        completionDate: new Date().toLocaleDateString(),
        reportUrl: 'https://dev.emm.aprco.com/reports/123',
      },
      'password-reset': {
        resetUrl: 'https://dev.emm.aprco.com/reset-password/token123',
      },
      'mfa-code': {
        verificationCode: '123456',
      },
      generic: {
        subject: 'Test Email',
        content: '<p>This is a test email from the Email Testing tool.</p>',
      },
    };
    return defaults[alias] || {};
  };

  const handleTemplateSelect = (alias: string) => {
    setSelectedTemplate(alias);
    setTemplateModel(getDefaultTemplateModel(alias));
    setShowPreview(false);
    setPreviewData(null);
  };

  const handlePreview = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template first');
      return;
    }
    setShowPreview(true);
  };

  const handleEdit = () => {
    // Try to get URL from preview data first, then fall back to the templates list
    const editUrl = previewData?.editUrl || templates?.find(t => t.alias === selectedTemplate)?.editUrl;

    if (!editUrl) {
      toast.error('Template edit URL not available. Ensure the Postmark token is valid.');
      return;
    }
    window.open(editUrl, '_blank', 'noopener,noreferrer');
  };

  // Update preview data when preview query completes
  React.useEffect(() => {
    if (preview) {
      setPreviewData(preview);
    }
  }, [preview]);

  const handleSendTest = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    sendEmailMutation.mutate({
      to: testEmail,
      templateAlias: selectedTemplate,
      templateModel,
    });
  };

  const updateTemplateModel = (key: string, value: any) => {
    setTemplateModel((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  if (templatesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-900">Email Delivery</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Manage and preview Postmark email templates and delivery settings.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Template Selection and Configuration */}
        <div className="space-y-6">
          {/* Template Selection */}
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">Select Template</h2>
              {selectedTemplate && (
                <button
                  onClick={handleEdit}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center"
                >
                  <PencilIcon className="w-4 h-4 mr-1.5" />
                  Edit Template
                </button>
              )}
            </div>
            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Choose a template...</option>
              {templates?.map((template) => (
                <option key={template.alias} value={template.alias}>
                  {template.name} {!template.active && '(Inactive)'}
                </option>
              ))}
            </select>
          </div>

          {/* Template Variables */}
          {selectedTemplate && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Template Variables</h2>
              <div className="space-y-4">
                {Object.entries(templateModel).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      {key}
                    </label>
                    <input
                      type="text"
                      value={String(value)}
                      onChange={(e) => updateTemplateModel(key, e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                ))}
                {Object.keys(templateModel).length === 0 && (
                  <p className="text-sm text-neutral-500">No variables for this template</p>
                )}
              </div>
            </div>
          )}

          {/* Test Email Configuration */}
          {selectedTemplate && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-4">Send Test Email</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handlePreview}
                    disabled={!selectedTemplate || previewLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <EyeIcon className="h-5 w-5" />
                    Preview
                  </button>
                  <button
                    onClick={handleSendTest}
                    disabled={!selectedTemplate || !testEmail || sendEmailMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendEmailMutation.isPending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-5 w-5" />
                        Send Test
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="space-y-6">
          {showPreview && previewLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {showPreview && previewError && (
            <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-red-900">Preview Error</h2>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-red-600">
                {(previewError as Error)?.message || 'Failed to load preview'}
              </p>
            </div>
          )}

          {showPreview && previewData && !previewLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-neutral-900">Email Preview</h2>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                  }}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Subject
                  </label>
                  <div className="px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-200">
                    {previewData.subject}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    HTML Preview
                  </label>
                  <div className="border border-neutral-200 rounded-lg overflow-hidden">
                    {previewData.htmlContent ? (
                      <iframe
                        srcDoc={previewData.htmlContent}
                        className="w-full h-96 border-0"
                        title="Email Preview"
                      />
                    ) : (
                      <div className="p-4 text-sm text-neutral-500">
                        No HTML content available for this template
                      </div>
                    )}
                  </div>
                </div>
                {previewData.textContent && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-neutral-700">
                      View Text Version
                    </summary>
                    <pre className="mt-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200 text-xs overflow-auto max-h-48">
                      {previewData.textContent}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {!showPreview && !previewLoading && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 flex items-center justify-center h-64">
              <div className="text-center">
                <EnvelopeIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                <p className="text-neutral-600">Select a template and click Preview to see the rendered email</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTesting;
